import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  Tooltip,
  Paper,
  Typography,
  Box,
  Button,
  IconButton,
  Divider,
  Chip,
  Card,
  CardContent,
  useTheme,
  ClickAwayListener,
  Popper,
  Fade,
  Portal
} from '@mui/material';
import {
  Info as InfoIcon,
  Close as CloseIcon,
  Launch as LaunchIcon,
  ContentCopy as CopyIcon,
  MoreHoriz as MoreIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';

/**
 * Styled tooltip components with Waltero branding
 */
const StyledTooltipContent = styled(Paper)(({ theme, variant, interactive }) => ({
  backgroundColor: theme.palette.background.paper,
  border: `1px solid ${theme.palette.waltero.primary.neutralBlue}`,
  borderRadius: '8px',
  boxShadow: '0px 4px 20px rgba(2, 56, 97, 0.15)',
  maxWidth: variant === 'rich' ? '400px' : '300px',
  overflow: 'hidden',
  
  ...(variant === 'simple' && {
    padding: theme.spacing(1, 1.5),
    backgroundColor: theme.palette.waltero.primary.deepBlue,
    color: theme.palette.common.white,
    border: 'none',
    fontSize: '0.875rem',
    lineHeight: 1.4
  }),
  
  ...(variant === 'info' && {
    backgroundColor: theme.palette.waltero.primary.backgroundBlue,
    borderColor: theme.palette.waltero.primary.lightBlue
  }),
  
  ...(variant === 'warning' && {
    backgroundColor: theme.palette.waltero.secondary.backgroundApricot,
    borderColor: theme.palette.waltero.secondary.lightApricot
  }),
  
  ...(variant === 'error' && {
    backgroundColor: '#ffeaea',
    borderColor: theme.palette.waltero.secondary.darkApricot
  }),
  
  ...(interactive && {
    cursor: 'pointer',
    transition: 'all 0.2s ease-in-out',
    
    '&:hover': {
      boxShadow: '0px 6px 24px rgba(2, 56, 97, 0.2)',
      transform: 'translateY(-1px)'
    }
  })
}));

const TooltipHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: theme.spacing(1.5, 2),
  backgroundColor: theme.palette.waltero.primary.backgroundBlue,
  borderBottom: `1px solid ${theme.palette.waltero.primary.neutralBlue}`,
  
  '& .title': {
    color: theme.palette.waltero.primary.coreBlue,
    fontWeight: 600,
    fontSize: '0.875rem'
  }
}));

const TooltipBody = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  
  '& .description': {
    color: theme.palette.waltero.primary.mediumBlue,
    fontSize: '0.875rem',
    lineHeight: 1.5,
    marginBottom: theme.spacing(1)
  },
  
  '& .metadata': {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(0.5),
    marginTop: theme.spacing(1)
  }
}));

const TooltipActions = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  gap: theme.spacing(1),
  padding: theme.spacing(1, 2),
  backgroundColor: theme.palette.waltero.primary.backgroundBlue,
  borderTop: `1px solid ${theme.palette.waltero.primary.neutralBlue}`
}));

/**
 * Enhanced WalteroTooltip Component
 * 
 * A sophisticated tooltip system with multiple variants and advanced features:
 * - Simple tooltips for basic help text
 * - Rich tooltips with structured content and actions
 * - Interactive tooltips with persistent display
 * - Contextual tooltips with dynamic content
 * - Accessibility compliant (WCAG 2.1 AA)
 * - Keyboard navigation support
 * 
 * @param {Object} props - Component properties
 * @param {React.ReactNode} props.children - Trigger element
 * @param {string|React.ReactNode} props.title - Tooltip title/content (for simple variant)
 * @param {string} [props.variant='simple'] - Tooltip variant ('simple', 'rich', 'interactive', 'info', 'warning', 'error')
 * @param {string} [props.placement='top'] - Tooltip placement
 * @param {boolean} [props.arrow=true] - Show arrow pointer
 * @param {boolean} [props.interactive=false] - Allow interaction with tooltip content
 * @param {boolean} [props.persistent=false] - Keep tooltip open until explicitly closed
 * @param {Function} [props.onOpen] - Callback when tooltip opens
 * @param {Function} [props.onClose] - Callback when tooltip closes
 * @param {Object} [props.richContent] - Rich content configuration for 'rich' variant
 * @param {Array} [props.actions] - Actions for interactive tooltips
 * @param {number} [props.delay=500] - Delay before showing tooltip (ms)
 * @param {number} [props.maxWidth=300] - Maximum tooltip width
 * @param {boolean} [props.disabled=false] - Disable tooltip
 */
const WalteroTooltip = ({
  children,
  title,
  variant = 'simple',
  placement = 'top',
  arrow = true,
  interactive = false,
  persistent = false,
  onOpen,
  onClose,
  richContent,
  actions = [],
  delay = 500,
  maxWidth = 300,
  disabled = false,
  ...otherProps
}) => {
  const theme = useTheme();
  const [open, setOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const triggerRef = useRef(null);
  const tooltipRef = useRef(null);
  const timeoutRef = useRef(null);

  // Handle tooltip open
  const handleOpen = useCallback((event) => {
    if (disabled) return;
    
    setAnchorEl(event.currentTarget);
    
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    
    timeoutRef.current = setTimeout(() => {
      setOpen(true);
      onOpen?.(event);
    }, persistent ? 0 : delay);
  }, [disabled, delay, persistent, onOpen]);

  // Handle tooltip close
  const handleClose = useCallback((event) => {
    if (persistent && event?.type !== 'click') return;
    
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    
    setOpen(false);
    setAnchorEl(null);
    onClose?.(event);
  }, [persistent, onClose]);

  // Handle click away for interactive tooltips
  const handleClickAway = useCallback((event) => {
    if (!interactive && !persistent) return;
    
    if (triggerRef.current?.contains(event.target) || 
        tooltipRef.current?.contains(event.target)) {
      return;
    }
    
    handleClose(event);
  }, [interactive, persistent, handleClose]);

  // Keyboard navigation
  const handleKeyDown = useCallback((event) => {
    if (event.key === 'Escape') {
      handleClose(event);
    }
  }, [handleClose]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  // Copy to clipboard action
  const handleCopyToClipboard = useCallback(async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      // Could show a brief success indicator here
    } catch (err) {
      console.error('Failed to copy to clipboard:', err);
    }
  }, []);

  // Render simple tooltip
  if (variant === 'simple' && !interactive && !persistent) {
    return (
      <Tooltip
        title={title}
        placement={placement}
        arrow={arrow}
        onOpen={onOpen}
        onClose={onClose}
        enterDelay={delay}
        disabled={disabled}
        componentsProps={{
          tooltip: {
            sx: {
              backgroundColor: theme.palette.waltero.primary.deepBlue,
              color: theme.palette.common.white,
              fontSize: '0.875rem',
              maxWidth: maxWidth,
              '& .MuiTooltip-arrow': {
                color: theme.palette.waltero.primary.deepBlue
              }
            }
          }
        }}
        {...otherProps}
      >
        {children}
      </Tooltip>
    );
  }

  // Render advanced tooltip content
  const renderTooltipContent = () => {
    switch (variant) {
      case 'rich':
        return (
          <StyledTooltipContent
            ref={tooltipRef}
            variant="rich"
            interactive={interactive}
            role="tooltip"
            aria-describedby="waltero-tooltip-content"
          >
            {richContent?.title && (
              <TooltipHeader>
                <Typography className="title" component="div">
                  {richContent.title}
                </Typography>
                {persistent && (
                  <IconButton
                    size="small"
                    onClick={handleClose}
                    aria-label="Close tooltip"
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                )}
              </TooltipHeader>
            )}
            
            <TooltipBody>
              {richContent?.description && (
                <Typography className="description" variant="body2">
                  {richContent.description}
                </Typography>
              )}
              
              {richContent?.metadata && (
                <Box className="metadata">
                  {Object.entries(richContent.metadata).map(([key, value]) => (
                    <Chip
                      key={key}
                      label={`${key}: ${value}`}
                      size="small"
                      variant="outlined"
                      sx={{
                        fontSize: '0.75rem',
                        height: '24px',
                        borderColor: theme.palette.waltero.primary.lightBlue,
                        color: theme.palette.waltero.primary.mediumBlue
                      }}
                    />
                  ))}
                </Box>
              )}
              
              {richContent?.content && (
                <Box mt={1}>
                  {richContent.content}
                </Box>
              )}
            </TooltipBody>
            
            {actions.length > 0 && (
              <TooltipActions>
                {actions.map((action, index) => (
                  <Button
                    key={index}
                    size="small"
                    variant={action.primary ? 'contained' : 'outlined'}
                    startIcon={action.icon}
                    onClick={action.onClick}
                    sx={{
                      fontSize: '0.75rem',
                      textTransform: 'none',
                      ...(action.primary && {
                        backgroundColor: theme.palette.waltero.secondary.primaryApricot,
                        '&:hover': {
                          backgroundColor: theme.palette.waltero.secondary.darkApricot
                        }
                      })
                    }}
                  >
                    {action.label}
                  </Button>
                ))}
              </TooltipActions>
            )}
          </StyledTooltipContent>
        );
      
      case 'info':
      case 'warning':
      case 'error':
        return (
          <StyledTooltipContent
            ref={tooltipRef}
            variant={variant}
            role="alert"
            aria-live="polite"
          >
            <TooltipBody>
              <Box display="flex" alignItems="flex-start" gap={1}>
                <InfoIcon 
                  fontSize="small"
                  sx={{ 
                    color: variant === 'info' ? theme.palette.waltero.primary.lightBlue :
                           variant === 'warning' ? theme.palette.waltero.secondary.primaryApricot :
                           theme.palette.waltero.secondary.darkApricot,
                    mt: 0.25
                  }}
                />
                <Box flex={1}>
                  {typeof title === 'string' ? (
                    <Typography variant="body2" className="description">
                      {title}
                    </Typography>
                  ) : (
                    title
                  )}
                </Box>
                {persistent && (
                  <IconButton
                    size="small"
                    onClick={handleClose}
                    aria-label="Close tooltip"
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                )}
              </Box>
            </TooltipBody>
          </StyledTooltipContent>
        );
      
      default:
        return (
          <StyledTooltipContent
            ref={tooltipRef}
            variant="simple"
            role="tooltip"
          >
            <Typography variant="body2">
              {title}
            </Typography>
          </StyledTooltipContent>
        );
    }
  };

  // Render interactive/persistent tooltip with Popper
  if (interactive || persistent || variant === 'rich') {
    return (
      <ClickAwayListener onClickAway={handleClickAway}>
        <Box display="inline-block">
          <Box
            ref={triggerRef}
            onMouseEnter={handleOpen}
            onMouseLeave={!persistent ? handleClose : undefined}
            onClick={persistent ? (open ? handleClose : handleOpen) : undefined}
            onKeyDown={handleKeyDown}
            tabIndex={persistent ? 0 : undefined}
            role={persistent ? 'button' : undefined}
            aria-expanded={persistent ? open : undefined}
            aria-haspopup={persistent ? 'true' : undefined}
          >
            {children}
          </Box>
          
          <Portal>
            <Popper
              open={open}
              anchorEl={anchorEl}
              placement={placement}
              transition
              modifiers={[
                {
                  name: 'flip',
                  enabled: true,
                  options: {
                    altBoundary: true,
                    rootBoundary: 'document',
                    fallbackPlacements: ['top', 'bottom', 'left', 'right'],
                  },
                },
                {
                  name: 'preventOverflow',
                  enabled: true,
                  options: {
                    altAxis: true,
                    altBoundary: true,
                    tether: true,
                    rootBoundary: 'document',
                  },
                },
                {
                  name: 'arrow',
                  enabled: arrow,
                },
              ]}
              style={{ zIndex: theme.zIndex.tooltip }}
            >
              {({ TransitionProps }) => (
                <Fade {...TransitionProps} timeout={200}>
                  <Box onKeyDown={handleKeyDown}>
                    {renderTooltipContent()}
                  </Box>
                </Fade>
              )}
            </Popper>
          </Portal>
        </Box>
      </ClickAwayListener>
    );
  }

  // Fallback to simple MUI tooltip
  return (
    <Tooltip
      title={title}
      placement={placement}
      arrow={arrow}
      onOpen={onOpen}
      onClose={onClose}
      enterDelay={delay}
      disabled={disabled}
      {...otherProps}
    >
      {children}
    </Tooltip>
  );
};

/**
 * Pre-configured tooltip variants for common use cases
 */

// Info tooltip with icon
export const WalteroInfoTooltip = ({ children, title, ...props }) => (
  <WalteroTooltip
    variant="info"
    title={title}
    placement="top"
    arrow={true}
    {...props}
  >
    {children}
  </WalteroTooltip>
);

// Warning tooltip
export const WalteroWarningTooltip = ({ children, title, ...props }) => (
  <WalteroTooltip
    variant="warning"
    title={title}
    placement="top"
    arrow={true}
    {...props}
  >
    {children}
  </WalteroTooltip>
);

// Error tooltip
export const WalteroErrorTooltip = ({ children, title, ...props }) => (
  <WalteroTooltip
    variant="error"
    title={title}
    placement="top"
    arrow={true}
    {...props}
  >
    {children}
  </WalteroTooltip>
);

// Rich interactive tooltip
export const WalteroRichTooltip = ({ children, richContent, actions = [], ...props }) => (
  <WalteroTooltip
    variant="rich"
    interactive={true}
    persistent={false}
    richContent={richContent}
    actions={actions}
    {...props}
  >
    {children}
  </WalteroTooltip>
);

// Help tooltip with question mark icon
export const WalteroHelpTooltip = ({ title, placement = 'top', ...props }) => (
  <WalteroTooltip
    title={title}
    placement={placement}
    arrow={true}
    {...props}
  >
    <IconButton
      size="small"
      sx={{
        color: theme => theme.palette.waltero.primary.lightBlue,
        '&:hover': {
          backgroundColor: theme => theme.palette.waltero.primary.paleBlue
        }
      }}
      aria-label="Help information"
    >
      <InfoIcon fontSize="small" />
    </IconButton>
  </WalteroTooltip>
);

// Data tooltip for showing contextual data information
export const WalteroDataTooltip = ({ 
  children, 
  title, 
  value, 
  unit, 
  timestamp, 
  copyable = false,
  ...props 
}) => {
  const actions = [];
  
  if (copyable && value) {
    actions.push({
      label: 'Copy',
      icon: <CopyIcon />,
      onClick: () => navigator.clipboard.writeText(String(value))
    });
  }

  const richContent = {
    title,
    description: `${value}${unit ? ` ${unit}` : ''}`,
    metadata: timestamp ? { 'Last Updated': new Date(timestamp).toLocaleString() } : undefined
  };

  return (
    <WalteroTooltip
      variant="rich"
      interactive={copyable}
      richContent={richContent}
      actions={actions}
      {...props}
    >
      {children}
    </WalteroTooltip>
  );
};

export default WalteroTooltip;